import {
  BatteryCharts,
  BatteryPowerType,
  CollapsibleChart,
  DeviceHeader,
  DeviceWithInstalledPower,
  GridConnectionChart,
  MarketPriceChart,
  OptimizationGroupParametersType,
  PVProductionChart,
} from '@e2m-customer-portal/domains';
import {
  useBatteryDeviceAcceptedForecasts,
  useBatteryForecastData,
  useDeviceTimeSeries,
  useFeedIn,
  useForecastData,
  useMarketLocationTimeSeries,
  useOptimizationGroupData,
  useTokenInfo,
} from '@e2m-customer-portal/hooks';
import { isCoLocation, isDefined, isSolar, MachineTechnology, UserRole } from '@e2m-customer-portal/shared';
import { ErrorComponent } from '@e2m-customer-portal/ui';
import { addDays, startOfDay } from 'date-fns';
import {
  Device,
  ForecastProperty,
  Interval,
  MarketLocation,
  MarketLocationTimeSeriesType,
  MarketLocationType,
  Resolution,
} from 'generated/graphql';
import { ReactElement, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import * as echarts from 'echarts/core';
import { EChartsReactRef } from 'react-echarts-library';

export const ECHARTS_CONTAINER_HEIGHT = 267;

interface ChartContainerProps {
  marketLocation: MarketLocation;
  dataInterval: Interval;
  currentForecastProperty?: ForecastProperty;
  batteries: DeviceWithInstalledPower[];
  solarDevices: DeviceWithInstalledPower[];
  compositeDevice?: Device;
  batteriesPower: Map<string, BatteryPowerType>;
  id?: string | null;
}

export function ChartContainer({
  marketLocation,
  dataInterval,
  currentForecastProperty,
  batteries,
  solarDevices,
  compositeDevice,
  batteriesPower,
  id,
}: Readonly<ChartContainerProps>): ReactElement<ChartContainerProps> {
  const [t] = useTranslation();
  const { roles } = useTokenInfo();

  const chartGroupId = 'colocation-overview-charts';

  const chartsInstanceStore = {} as Record<string, echarts.EChartsType>;

  const [isZoomed, setIsZoomed] = useState<boolean>(false);

  function onChartReadyCallback(instance: echarts.EChartsType) {
    if (instance) {
      if (!chartsInstanceStore[instance.id]) {
        instance.group = chartGroupId;
        chartsInstanceStore[instance.id] = instance;
      }
      instance.dispatchAction({
        type: 'takeGlobalCursor',
        key: 'dataZoomSelect',
        // Activate or inactivate.
        dataZoomSelectActive: true,
      });
    }
    for (const [key, value] of Object.entries(chartsInstanceStore)) {
      if (!value) {
        delete chartsInstanceStore[key];
      }
    }

    if (Object.entries(chartsInstanceStore).length > 1) {
      echarts.disconnect(chartGroupId);
      echarts.connect(chartGroupId);
    }
  }

  const handleEvents = {
    datazoom: () => {
      setIsZoomed(true);
    },
  };

  const resetZoom = (ref: EChartsReactRef) => {
    if (!ref) return;
    ref.getEchartsInstance()?.dispatchAction({
      type: 'dataZoom',
      start: 0, // percentage of starting position; 0 - 100
      end: 100, // percentage of ending position; 0 - 100
    });
    setIsZoomed(false);
  };

  const [acceptedForecasts, acceptedForecastError] = useBatteryDeviceAcceptedForecasts(
    batteries.map((battery) => battery.internalId),
    dataInterval,
  );

  const [forecasts, forecastsError] = useBatteryForecastData(
    batteries.map((battery) => battery.id),
    dataInterval,
  );

  const [solarForecasts, solarForecastsError] = useForecastData(
    solarDevices.map((device) => device.id),
    { start: dataInterval.start, end: addDays(startOfDay(dataInterval.end), 1) },
    Resolution.FIFTEEN_MINUTES,
  );

  const [feedIn, feedInError] = useFeedIn(
    [marketLocation, marketLocation.linkedMarketLocation]
      .filter(isDefined)
      .map((maLo) => ({ id: maLo.id, type: maLo.type })),
    {
      ...dataInterval,
      end: startOfDay(addDays(dataInterval.end, 1)),
    },
  );

  const feedInData = new Map(
    [marketLocation, marketLocation.linkedMarketLocation]
      .filter(isDefined)
      .map((maLo) => [
        maLo.type ?? MarketLocationType.PRODUCER,
        feedIn?.find((feedIn) => feedIn.marketLocationId === maLo.id),
      ]),
  );

  const [deviceTimeSeries, deviceTimeSeriesError] = useDeviceTimeSeries(
    [compositeDevice, ...solarDevices, ...batteries].map((battery) => battery?.internalId).filter(isDefined),
    dataInterval,
  );

  const marketLocationNumbers = [marketLocation.number, marketLocation.linkedMarketLocation?.number].filter(isDefined);

  const [calculatedPVProductionTimeSeries, calculatedPVProductionTimeSeriesError] = useMarketLocationTimeSeries(
    marketLocationNumbers,
    [MarketLocationTimeSeriesType.CALCULATED_PV_PRODUCTION],
    dataInterval,
  );

  const { data } = useOptimizationGroupData(id ?? '');

  const optimizationGroupData: OptimizationGroupParametersType = data?.json && JSON.parse(data.json);

  return (
    <div className="relative w-full flex flex-col items-start">
      <CollapsibleChart title={t('COLOCATION_OVERVIEW_CHARTS_MARKET_PRICES')}>
        <ErrorBoundary FallbackComponent={ErrorComponent}>
          <MarketPriceChart
            dataInterval={dataInterval}
            onChartReadyCallback={onChartReadyCallback}
            resetZoom={resetZoom}
            //because the charts are connected we need to catch the zoom event only once
            handleEvents={handleEvents}
            isZoomed={isZoomed}
          />
        </ErrorBoundary>
      </CollapsibleChart>
      <CollapsibleChart
        title={
          roles.has(UserRole.VPPOperator) && compositeDevice
            ? `${t('COLOCATION_OVERVIEW_CHARTS_GRID_CONNECTION')} (${compositeDevice.name})`
            : t('COLOCATION_OVERVIEW_CHARTS_GRID_CONNECTION')
        }
      >
        <ErrorBoundary FallbackComponent={ErrorComponent}>
          <GridConnectionChart
            dataInterval={dataInterval}
            deviceTimeSeries={deviceTimeSeries?.find(
              (timeSeries) => timeSeries.deviceNumber === compositeDevice?.internalId,
            )}
            deviceTimeSeriesError={deviceTimeSeriesError}
            feedIn={feedInData}
            feedInError={feedInError}
            onChartReadyCallback={onChartReadyCallback}
            resetZoom={resetZoom}
            isZoomed={isZoomed}
          />
        </ErrorBoundary>
      </CollapsibleChart>
      {(isSolar(marketLocation.technologies.concat(marketLocation.linkedMarketLocation?.technologies ?? [])) ||
        isCoLocation(marketLocation.technologies.concat(marketLocation.linkedMarketLocation?.technologies ?? []))) && (
        <CollapsibleChart title={t('COLOCATION_OVERVIEW_CHARTS_PV_PRODUCTION')}>
          <ErrorBoundary FallbackComponent={ErrorComponent}>
            {marketLocationNumbers.map((number) => (
              <PVProductionChart
                key={number}
                dataInterval={dataInterval}
                calculatedPVProductionTimeSeries={calculatedPVProductionTimeSeries?.find(
                  (timeSeries) => timeSeries.marketLocationNumber === number,
                )}
                calculatedPVProductionTimeSeriesError={calculatedPVProductionTimeSeriesError}
                deviceTimeSeries={deviceTimeSeries?.filter((timeSeries) =>
                  solarDevices.map((device) => device.internalId).includes(timeSeries.deviceNumber),
                )}
                deviceTimeSeriesError={deviceTimeSeriesError}
                forecasts={solarForecasts}
                forecastError={solarForecastsError}
                onChartReadyCallback={onChartReadyCallback}
                resetZoom={resetZoom}
                isZoomed={isZoomed}
              />
            ))}
          </ErrorBoundary>
        </CollapsibleChart>
      )}
      {solarDevices.map((device) => (
        <DeviceHeader
          marketLocation={marketLocation}
          currentForecastProperty={currentForecastProperty}
          key={device.id}
          device={device}
          id={id}
          optimizationGroupData={optimizationGroupData}
          technology={MachineTechnology.SOLAR}
        >
          <CollapsibleChart title={t('COLOCATION_OVERVIEW_CHARTS_PV_PRODUCTION')}>
            <ErrorBoundary FallbackComponent={ErrorComponent}>
              <PVProductionChart
                dataInterval={dataInterval}
                deviceTimeSeries={deviceTimeSeries?.filter(
                  (timeSeries) => timeSeries.deviceNumber === device.internalId,
                )}
                device={device}
                deviceTimeSeriesError={deviceTimeSeriesError}
                forecasts={solarForecasts?.filter((forecast) => forecast.deviceId === device.id)}
                forecastError={solarForecastsError}
                onChartReadyCallback={onChartReadyCallback}
                resetZoom={resetZoom}
                isZoomed={isZoomed}
              />
            </ErrorBoundary>
          </CollapsibleChart>
        </DeviceHeader>
      ))}
      {batteries.map((device) => (
        <DeviceHeader
          marketLocation={marketLocation}
          key={device.id}
          device={device}
          currentForecastProperty={currentForecastProperty}
          id={id}
          optimizationGroupData={optimizationGroupData}
          technology={MachineTechnology.BATTERY}
        >
          <ErrorBoundary FallbackComponent={ErrorComponent}>
            <BatteryCharts
              dataInterval={dataInterval}
              device={device}
              forecasts={[forecasts, forecastsError]}
              acceptedForecasts={[acceptedForecasts, acceptedForecastError]}
              deviceTimeSeries={[deviceTimeSeries, deviceTimeSeriesError]}
              batteriesPower={batteriesPower}
              onChartReadyCallback={onChartReadyCallback}
              resetZoom={resetZoom}
              isZoomed={isZoomed}
            />
          </ErrorBoundary>
        </DeviceHeader>
      ))}
    </div>
  );
}
